/**
 * SchedulesManager
 *
 * Lists, creates, edits, toggles, and deletes an agent's recurring schedules.
 * Designed to be embedded inline (detail page) or inside a dialog (list card).
 *
 * Manual-report agents (the analyst) require a reporting window per schedule; the
 * sync worker runs the hardcoded tracker for those, not the LLM agent.
 * Email delivery is still sent from the UI for now.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import {
  listPlaybookSchedules,
  createPlaybookSchedule,
  updatePlaybookSchedule,
  deletePlaybookSchedule,
} from '../../../services/playbooksService';
import type {
  CreatePlaybookScheduleRequest,
  PlaybookSchedule,
  SchedulePeriodWindow,
} from '../../../types/playbooks';
import { SnackbarContext } from '../../../ui/SnackbarProvider';
import { ConfirmDialog } from '../../../components/dialog';
import ScheduleFormDialog from './ScheduleFormDialog';

const WEEKDAYS_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const WEEKDAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function describeSchedule(s: PlaybookSchedule): string {
  const time = s.time_of_day;
  if (s.cadence === 'daily') return `Every day at ${time}`;
  if (s.cadence === 'weekly') {
    const day = WEEKDAYS_LONG[s.day_of_week ?? 0] ?? 'Monday';
    return `Every ${day} at ${time}`;
  }
  if (s.cadence === 'monthly') {
    return `On the ${ordinal(s.day_of_month ?? 1)} of each month at ${time}`;
  }
  return time;
}

export function describePeriodWindow(window: SchedulePeriodWindow | null | undefined): string | null {
  if (!window || window.type !== 'week_relative') return null;
  const start = WEEKDAYS_SHORT[window.start_weekday] ?? '?';
  const end = WEEKDAYS_SHORT[window.end_weekday] ?? '?';
  const week = window.week === 'previous' ? 'Previous week' : 'This week';
  return `${week} ${start}–${end}`;
}

function shortTz(tz: string): string {
  return tz.includes('/') ? tz.split('/').pop()!.replace(/_/g, ' ') : tz;
}

export interface SchedulesManagerProps {
  playbookId: string;
  /** When true, schedules must include a reporting window (manual pipelines). */
  requirePeriodWindow?: boolean;
  /** Surface a count change to parents (e.g. to refresh a card chip). */
  onCountChange?: (count: number) => void;
}

export default function SchedulesManager({
  playbookId,
  requirePeriodWindow = false,
  onCountChange,
}: SchedulesManagerProps) {
  const { notify } = useContext(SnackbarContext);
  const [schedules, setSchedules] = useState<PlaybookSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PlaybookSchedule | null>(null);
  const [saving, setSaving] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<PlaybookSchedule | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PlaybookSchedule | null>(null);

  // Keep the latest callback without making `refresh` re-create on every parent
  // render — otherwise the load effect refires in a loop (modal flicker).
  const onCountChangeRef = useRef(onCountChange);
  useEffect(() => {
    onCountChangeRef.current = onCountChange;
  }, [onCountChange]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listPlaybookSchedules(playbookId);
      setSchedules(rows);
      onCountChangeRef.current?.(rows.length);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }, [playbookId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleSubmit = async (payload: CreatePlaybookScheduleRequest) => {
    setSaving(true);
    try {
      if (editing) {
        await updatePlaybookSchedule(playbookId, editing.schedule_id, payload);
        notify('Schedule updated', 'success');
      } else {
        await createPlaybookSchedule(playbookId, payload);
        notify('Schedule created', 'success');
      }
      setFormOpen(false);
      setEditing(null);
      await refresh();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to save schedule', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (s: PlaybookSchedule) => {
    // Optimistic flip
    setSchedules((prev) =>
      prev.map((x) => (x.schedule_id === s.schedule_id ? { ...x, enabled: !x.enabled } : x)),
    );
    try {
      await updatePlaybookSchedule(playbookId, s.schedule_id, { enabled: !s.enabled });
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to update schedule', 'error');
      await refresh();
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    try {
      await deletePlaybookSchedule(playbookId, target.schedule_id);
      notify('Schedule deleted', 'success');
      await refresh();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to delete schedule', 'error');
    }
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuTarget(null);
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        {requirePeriodWindow
          ? 'Schedules on this agent run the manual tracker (not the LLM agent). Pick a reporting window for each schedule — e.g. Monday → previous week Mon–Sun, Thursday → this week Mon–Wed. Email delivery is still sent from the UI for now.'
          : 'Schedules fire this agent automatically. Email delivery is still sent from the UI for now.'}
      </Alert>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {schedules.length === 0
            ? 'No schedules yet'
            : `${schedules.length} schedule${schedules.length === 1 ? '' : 's'}`}
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddRounded />}
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          New schedule
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Alert severity="error" action={<Button onClick={() => void refresh()}>Retry</Button>}>
          {error}
        </Alert>
      ) : schedules.length === 0 ? (
        <Box
          sx={{
            border: (t) => `1px dashed ${t.palette.divider}`,
            borderRadius: 1,
            py: 4,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <ScheduleRounded sx={{ fontSize: 32, opacity: 0.5, mb: 1 }} />
          <Typography variant="body2">
            Create a schedule to email this agent's output on a recurring basis.
          </Typography>
        </Box>
      ) : (
        <Stack divider={<Divider flexItem />} spacing={0}>
          {schedules.map((s) => (
            <Stack
              key={s.schedule_id}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ py: 1.5, opacity: s.enabled ? 1 : 0.55 }}
            >
              <Tooltip title={s.enabled ? 'Enabled' : 'Disabled'}>
                <Switch
                  size="small"
                  checked={s.enabled}
                  onChange={() => void handleToggle(s)}
                />
              </Tooltip>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {s.label || describeSchedule(s)}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" noWrap>
                  {s.label ? `${describeSchedule(s)} · ` : ''}
                  {describePeriodWindow(s.period_window)
                    ? `${describePeriodWindow(s.period_window)} · `
                    : ''}
                  {shortTz(s.timezone)} · {s.recipient_emails.length} recipient
                  {s.recipient_emails.length === 1 ? '' : 's'}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {s.recipient_emails.slice(0, 3).map((e) => (
                    <Chip key={e} label={e} size="small" variant="outlined" />
                  ))}
                  {s.recipient_emails.length > 3 && (
                    <Chip label={`+${s.recipient_emails.length - 3}`} size="small" variant="outlined" />
                  )}
                </Stack>
              </Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  setMenuAnchor(e.currentTarget);
                  setMenuTarget(s);
                }}
              >
                <MoreVertRounded fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            setEditing(menuTarget);
            setFormOpen(true);
            closeMenu();
          }}
        >
          <EditRounded fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setPendingDelete(menuTarget);
            closeMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteRounded fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      <ScheduleFormDialog
        open={formOpen}
        schedule={editing}
        requirePeriodWindow={requirePeriodWindow}
        saving={saving}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete schedule?"
        description="This schedule will stop running. This cannot be undone."
        confirmLabel="Delete"
        tone="destructive"
        onConfirm={handleDelete}
        onClose={() => setPendingDelete(null)}
      />
    </Box>
  );
}
